import {useEffect, useState, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {getCoordinatorDetail} from '../../../api/coordiApi';
import AreaSelectComponent from '../../../components/common/AreaSelectComponent';
import defaultMaleProfileImg from '../../../assets/images/man_default_img.png';
import defaultFemaleProfileImg from '../../../assets/images/woman_default_img.png';
import {CoordinatorDetailData} from "../../../interface/CoordinatorData";
import {defaultAlert} from "../../../utils/alert";
import {Loading} from "../../../components/common/Loading";


export default function CoordinatorApplyDetailPage() {
    const navigate = useNavigate();
    const [data, setData] = useState<CoordinatorDetailData | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedMainArea, setSelectedMainArea] = useState<string>("");
    const [selectedSubArea, setSelectedSubArea] = useState<string>("");
    const [settlement, setSettlement] = useState("PERSONAL");
    const [bizNo, setBizNo] = useState("");
    const [bizCertFileName, setBizCertFileName] = useState("");
    const [bizCertFileUrl, setBizCertFileUrl] = useState("");
    const [bizCertFile, setBizCertFile] = useState<File | null>(null);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const onChangeSettlement = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setSettlement(next);
    };
    const onChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setBizCertFile(file);
        setBizCertFileName(file ? file.name : "");
    };
    const onDeleteFile = () => {
        setBizCertFileName("");
        setBizCertFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    useEffect(() => {
        fetchDetail();
    }, []);

    const fetchDetail = async () => {
        try {
            const res = await getCoordinatorDetail();
            if (res.data?.data) {
                setData(res.data.data);
                setSelectedMainArea(res.data.data.preferredAreaMajor || '');
                setSelectedSubArea(res.data.data.preferredAreaMinor || '');

                setSettlement(res.data.data.settlement || "PERSONAL");
                setBizNo(res.data.data.bizNo || "");
                setBizCertFileName(res.data.data.bizCertFileName || "");
                setBizCertFileUrl(res.data.data.bizCertFileUrl || "");
                setBizCertFileName(res.data.data.bizCertFileName || "");
            }
        } catch (error) {
            console.error('Failed to fetch coordinator detail:', error);
            defaultAlert('코디네이터 정보를 불러오는데 실패했습니다.');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    // 상태 표시
    const getStatusText = (status: string) => {
        switch (status) {
            case 'REGISTERED':
                return '심사중';
            case 'COMPLETE':
                return '승인';
            case 'IMPOSSIBLE':
                return '거절';
            case 'DENY':
                return '보완';
            default:
                return '알 수 없음';
        }
    };

    // 상태 색상
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'REGISTERED':
                return '#FFA500'; // 주황색
            case 'DENY':
                return '#FFA500'; // 주황색
            case 'COMPLETE':
                return '#4CAF50'; // 녹색
            case 'IMPOSSIBLE':
                return '#F44336'; // 빨간색
            default:
                return '#999';
        }
    };

    // 카테고리 표시
    const getCategoryText = (category: string) => {
        return category === 'GENERAL' ? '일반 간병인' : '요양보호사';
    };

    // 경력 표시
    const getCareerText = (hasCareer: boolean, year: number, month: number) => {
        if (!hasCareer) return '미경력';

        const parts = [];
        if (year > 0) parts.push(`${year}년`);
        if (month > 0) parts.push(`${month}개월`);

        return parts.length > 0 ? parts.join(' ') : '미경력';
    };

    if (!data) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <div>데이터를 불러올 수 없습니다.</div>
            </div>
        );
    }

    return (
        <>
            <Loading loading={loading} onClose={() => setLoading(false)}/>
            <main className="container type_gray no-bottom-nav">
                {/* 심사 상태 */}
                <section className="bg-white">
                    <div className="inner-box">
                        <div
                            className="step-guide-box"
                            style={{color: getStatusColor(data.status)}}
                        >
                            {getStatusText(data.status)}
                        </div>
                    </div>
                </section>

                {/* 코디 정보 */}
                <section className="bg-white">
                    <div className="inner-box">
                        <h3 className="cont-tit">
                            <span className="underline">코디 정보</span>
                        </h3>
                        <div className="round-box mt10">
                            <div className="round-body">
                                <div className="info-row">
                                    <span className="label">구분</span>
                                    <span className="cont">{getCategoryText(data.category)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 기본 정보 */}
                <section className="bg-white">
                    <div className="inner-box">
                        <h3 className="cont-tit">
                            <span className="underline">기본 정보</span>
                        </h3>
                        <div className="round-box mt10">
                            <div className="round-body">
                                <div className="info-row">
                                    <span className="label">이름</span>
                                    <span className="cont">{data.memberName}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">연락처</span>
                                    <span className="cont">{data.phone}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">국적</span>
                                    <span className="cont">{data.nationalityName || '-'}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">선호 지역</span>
                                    <span className="cont">
                                            <AreaSelectComponent
                                                selectedMainArea={selectedMainArea}
                                                selectedSubArea={selectedSubArea}
                                                onMainAreaChange={setSelectedMainArea}
                                                onSubAreaChange={setSelectedSubArea}
                                                required={true}
                                                disabled={true}
                                            />
                                            </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 경력 정보 */}
                <section className="bg-white">
                    <div className="inner-box">
                        <h3 className="cont-tit">
                            <span className="underline">경력 정보</span>
                        </h3>
                        <div className="round-box mt10">
                            <div className="round-body">
                                <div className="info-row">
                                    <span className="label">경력</span>
                                    <span className="cont">
                                    {getCareerText(data.hasCareer, data.careerYear, data.careerMonth)}
                                </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 정산 유형 */}
                <section className="bg-white">
                    <fieldset>
                        <legend>정산 유형</legend>
                        <div className="inner-box">
                            <h3 className="cont-tit">
                                <span className="underline">정산 유형</span>
                            </h3>
                            <div className="form-item">
                                <div className="radio-row">
                                        <span className="text-radio">
                                            <input
                                                type="radio"
                                                id="radio11"
                                                name="settlement"
                                                value="PERSONAL"
                                                checked={settlement === "PERSONAL"}
                                                onChange={onChangeSettlement}
                                                disabled={true}
                                            />
                                            <label htmlFor="radio11">개인</label>
                                        </span>
                                    <span className="text-radio">
                                            <input
                                                type="radio"
                                                id="radio12"
                                                name="settlement"
                                                value="BUSINESS"
                                                checked={settlement === "BUSINESS"}
                                                onChange={onChangeSettlement}
                                                disabled={true}
                                            />
                                            <label htmlFor="radio12">사업자</label>
                                        </span>
                                </div>
                                {settlement === "BUSINESS" && (
                                    <>
                                        <div className="input-box">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                placeholder="사업자등록번호를 입력해 주세요"
                                                value={bizNo}
                                                onChange={(e) => setBizNo(e.target.value.replace(/\D/g, ""))}
                                                disabled={true}
                                            />
                                        </div>
                                        <div className="input-box">
                                            <div className="file-upload">
                                                <input
                                                    type="text"
                                                    className="upload-name"
                                                    value={bizCertFileName}
                                                    placeholder="사업자등록증을 첨부해 주세요"
                                                    readOnly
                                                    disabled={true}
                                                />
                                                <input
                                                    type="file"
                                                    id="biz_cert_file"
                                                    className="upload-hidden"
                                                    ref={fileInputRef}
                                                    onChange={onChangeFile}
                                                    disabled={true}
                                                />
                                                <button
                                                    type="button"
                                                    className="delete"
                                                    onClick={onDeleteFile}
                                                    disabled={true}
                                                >
                                                    <span className="blind">삭제</span>
                                                    <i className="fa-regular fa-trash-can"></i>
                                                </button>
                                            </div>
                                            <label
                                                htmlFor="biz_cert_file"
                                                className="inp-btn"
                                                style={{pointerEvents: 'none', opacity: 0.5}}
                                            >
                                                <span>파일찾기</span>
                                            </label>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </fieldset>
                </section>


                {/* 코디 소개 */}
                <section className="bg-white">
                    <div className="inner-box">
                        <h3 className="cont-tit">
                            <span className="underline">코디 소개</span>
                        </h3>
                        <div className="guide-box gray">
                            <p className="guide_txt" style={{whiteSpace: 'pre-wrap'}}>
                                {data.introduction || '소개글이 없습니다.'}
                            </p>
                        </div>
                    </div>
                </section>

                {/* 프로필 이미지 */}
                <section className="bg-white">
                    <div className="inner-box">
                        <h3 className="cont-tit">
                            <span className="underline">프로필 이미지</span>
                        </h3>
                        <div className="form-item">
                            <div className="thumbnail-wrap profileImg">
                                <div className="thumbnail">
                                    <div className="centered">
                                        <img
                                            src={data.imagePath && data.imagePath !== 'default' ? data.imagePath : (data.gender === 'M' || data.gender === '남성' ? defaultMaleProfileImg : defaultFemaleProfileImg)}
                                            className="landscape"
                                            alt="프로필"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = data.gender === 'M' || data.gender === '남성' ? defaultMaleProfileImg : defaultFemaleProfileImg;
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 자격증 */}
                <section className="bg-white">
                    <div className="inner-box">
                        <h3 className="cont-tit">
                            <span className="underline">자격증</span>
                        </h3>
                        {data.certificates && data.certificates.length > 0 ? (
                            <div className="round-box mt10">
                                <div className="round-body">
                                    {data.certificates.map((cert, index) => (
                                        <div key={index} className="info-row">
                                            <span className="label">{cert.certificateName}</span>
                                            <span className="cont">{cert.certificateIssuer}
                                                <span className="date">({cert.issueDate})</span>
                                                        </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="guide-box gray">
                                <p className="guide_txt">등록된 자격증이 없습니다.</p>
                            </div>
                        )}
                    </div>
                </section>
            </main>
        </>
    );
}